<script lang="ts" setup>
import { useI18n } from '#i18n';
import { XSpinner } from '@clicksign/excalibur';

import { Decisions, useFeature } from '~~core';

import CapturePreview from '../CapturePreview/CapturePreview.vue';

type FacialBiometricsFacematchStatusProps = {
  loading?: boolean,
  picture?: Blob,
  remainingAttempts?: number;
};

type FacialBiometricsFacematchStatusEmit = {
  (event: 'try-again'): void;
};

const emit = defineEmits<FacialBiometricsFacematchStatusEmit>();

const props = withDefaults(defineProps<FacialBiometricsFacematchStatusProps>(), {
  loading: false,
  picture: undefined,
});

const { loading } = toRefs(props);
const { t } = useI18n();

// TODO: check if limit is exceeded
const { isEnabled: isFacematchRetriesLimitEnabled } = useFeature(Decisions.TOGGLE_FACEMATCH_RETRIES_LIMIT);

const title = computed(() => t('requirement.liveness.modal.capture.title.error'));
// TOGGLE_FACEMATCH_RETRIES_LIMIT
// TODO: when toggle is removed remove the isFacematchRetriesLimitEnabled check
const subtitle = computed(() => (isFacematchRetriesLimitEnabled.value ? t('requirement.facialBiometrics.facematch.limit', [props.remainingAttempts]) : t('requirement.facialBiometrics.error.facematch.subtitle')));

// TOGGLE_FACEMATCH_RETRIES_LIMIT
// TODO: when toggle is removed remove the isFacematchRetriesLimitEnabled check
const errorMessage = computed(() => (isFacematchRetriesLimitEnabled.value ? t('requirement.facialBiometrics.facematch.exceededLimit') : t('requirement.facialBiometrics.error.facematch.title')));
</script>

<template>
  <section :class="$style.overlay">
    <div
      v-if="loading"
      :class="[$style.container, $style.loading]"
      data-testid="facematchStatusLoading"
    >
      <XSpinner variant="light" />
      <h4 :class="$style.title">
        <span>
          {{ t('requirement.facialBiometrics.facematch.loading') }}
        </span>
      </h4>
    </div>
    <!-- TOGGLE_FACEMATCH_RETRIES_LIMIT
      TODO: remove the isFacematchRetriesLimitEnabled check
    -->
    <CapturePreview
      v-else
      :has-error="true"
      :error-message="errorMessage"
      :is-loading="false"
      :picture="picture"
      :can-show-security="isFacematchRetriesLimitEnabled"
      :class="$style.containerPreview"
      @try-again="emit('try-again')"
    >
      <template
        v-if="!isFacematchRetriesLimitEnabled"
        #title
      >
        <h4 :class="$style.title">
          {{ title }}
        </h4>
      </template>

      <template #sub-error>
        <p
          :class="{
            [$style.sub]: true,
            [$style.limit]: isFacematchRetriesLimitEnabled
          }"
        >
          {{ subtitle }}
        </p>
      </template>
    </CapturePreview>
  </section>
</template>

<style lang="scss" module>
.containerPreview {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  margin-top: var(--space-large-xxx);
}

.overlay {
  position: fixed;
  z-index: 1;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  color: var(--color-neutral-0);

  /**
    * ISSUE: https://github.com/clicksign/excalibur/issues/814
    * TODO: atualiza variável do background
    */
  background-color: var(--color-transparent-1000-95);
}

.container {
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
  max-width: 460px;
  gap: var(--space-regular);
  text-align: center;
}

.title {
  margin: var(--space-medium) 0;
}

.sub{
  margin-top: var(--space-regular);
}

.limit {
  margin-top: var(--space-medium);
  font-weight: var(--font-weight-bold);
}
</style>
